import React, { useEffect, useState, useRef } from "react";
import Layout from "../Layout/Layout";
import { Button } from "../../components/ui/Button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { useTitle } from "../../config/global";
import axiosInstance from "../../config/Axios";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../utils/datetime";
import { useInHub } from '../../nav/useInHub';

const TYPE_SEVERITY = { silent_visual: "success", full_video: "info", audio_only: "warning", corner_popup: "secondary" };

function StatCard({ icon, label, value, sub, color = "primary" }) {
    return (
        <div className="col-12 col-sm-6 col-xl-3">
            <div className="card">
                <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between">
                        <div className="content-left">
                            <span className="text-muted text-sm">{label}</span>
                            <div className="d-flex align-items-end mt-2 pt-1 flex-wrap">
                                <h4 className={`mb-0 me-2 text-${color}`}>{value}</h4>
                                {sub && <small className="text-muted">{sub}</small>}
                            </div>
                        </div>
                        <div className={`avatar bg-label-${color}`}>
                            <span className="avatar-initial rounded"><i className={`bx ${icon} fs-4`}></i></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function BarChart({ data, noDataLabel }) {
    if (!data || data.length === 0) return <p className="text-muted text-sm text-center py-4">{noDataLabel}</p>;
    const max = Math.max(...data.map(d => d.points), 1);
    return (
        <div className="d-flex align-items-end gap-1" style={{ height: 100 }}>
            {data.slice(-14).map((d, i) => (
                <div key={i} className="flex-fill d-flex flex-column align-items-center gap-1" title={`${d.date}: ${d.points} pts`}>
                    <div
                        className="w-100 rounded-top bg-primary"
                        style={{ height: `${(d.points / max) * 90}%`, minHeight: 2, opacity: 0.8 }}
                    />
                    <span className="text-muted" style={{ fontSize: 8 }}>{d.date?.slice(-2)}</span>
                </div>
            ))}
        </div>
    );
}

export default function AdEarnings() {
    const { t } = useTranslation();
  const inHub = useInHub();
    useTitle("Ad Earnings");
    const toast  = useRef(null);
    const [earnings, setEarnings]           = useState(null);
    const [impressions, setImpressions]     = useState([]);
    const [impPage, setImpPage]             = useState(1);
    const [impMeta, setImpMeta]             = useState({});
    const [pointsBalance, setPointsBalance] = useState(0);
    const [loading, setLoading]             = useState(true);
    const [redeeming, setRedeeming]         = useState(false);
    const [redeemAmount, setRedeemAmount]   = useState("");
    const [lastUpdated, setLastUpdated]     = useState(null);
    const [secondsAgo, setSecondsAgo]       = useState(0);

    useEffect(() => {
        // ⚠️ CANCELLED, NOT JUST CLEARED. The interval outlives a slow request:
        // unmount (or a page change) while one is in flight and the response
        // still lands, calling setState on a component that is gone. `alive`
        // makes the late response a no-op instead of a React warning — and stops
        // a stale page's numbers overwriting a fresh one's.
        let alive = true;

        const load = async (isRefresh = false) => {
            // ⚠️ allSettled, NOT all — THIS PAGE RENDERED EMPTY FOR MONTHS BECAUSE
            // OF THE DIFFERENCE. Promise.all rejects as soon as ONE call fails, and
            // the catch below is silent, so a single bad sibling wiped earnings,
            // balance and impressions together. Both siblings were in fact failing:
            // /ad-marketplace/earnings 404'd (the route was never registered, though
            // the service behind it was written and correct) and /my/points 500'd on
            // a missing deleted_at column. The earnings data was fine the whole time
            // and was thrown away on arrival.
            //
            // Each result is now read on its own. One broken endpoint costs its own
            // card, not the page.
            const [earningsRes, pointsRes, imprRes] = await Promise.allSettled([
                axiosInstance.get("/ad-marketplace/earnings"),
                axiosInstance.get("/ad-marketplace/my/points"),
                axiosInstance.get("/ad-marketplace/my/impressions", { params: { page: impPage, per_page: 10 } }),
            ]);

            if (!alive) return;

            if (earningsRes.status === "fulfilled") setEarnings(earningsRes.value.data?.data ?? null);
            if (pointsRes.status === "fulfilled") setPointsBalance(pointsRes.value.data?.data?.balance ?? 0);
            if (imprRes.status === "fulfilled") {
                setImpressions(imprRes.value.data?.data ?? []);
                setImpMeta(imprRes.value.data?.meta ?? {});
            }

            // Only stamp a refresh that actually returned something. A poll that
            // failed on every endpoint must not advance "updated Ns ago" — the
            // whole point of the line is to say how stale these numbers are.
            if (earningsRes.status === "fulfilled" || imprRes.status === "fulfilled") {
                setLastUpdated(Date.now());
                setSecondsAgo(0);
            }

            // ⚠️ THE SPINNER IS FIRST-LOAD ONLY. Flipping `loading` on every poll
            // would replace the whole page with a spinner twice a minute while a
            // café is reading its own earnings.
            if (!isRefresh) setLoading(false);
        };

        load();

        // Impressions arrive from cafés continuously; 30s is frequent enough to
        // feel live and cheap enough that a page left open all day is ~2 req/min.
        const poll = setInterval(() => load(true), 30000);

        return () => {
            alive = false;
            clearInterval(poll);
        };
    }, [impPage]);

    // Ticks the "updated Ns ago" label between polls. Separate from the fetch so
    // a slow or failed request still shows the age honestly climbing.
    useEffect(() => {
        if (!lastUpdated) return undefined;
        const tick = setInterval(() => setSecondsAgo(Math.floor((Date.now() - lastUpdated) / 1000)), 1000);
        return () => clearInterval(tick);
    }, [lastUpdated]);

    const redeem = async () => {
        const amount = parseInt(redeemAmount, 10);
        if (!amount || amount < 1) return;
        setRedeeming(true);
        try {
            await axiosInstance.post("/ad-marketplace/my/points/redeem", { amount });
            toast.current?.show({ severity: "success", summary: t('billing.redeemed'), detail: t('billing.points_redeemed', { count: amount }), life: 3000 });
            setPointsBalance(b => Math.max(0, b - amount));
            setRedeemAmount("");
        } catch (err) {
            toast.current?.show({ severity: "error", summary: t('billing.error'), detail: err?.response?.data?.msg ?? t('billing.redemption_failed'), life: 3000 });
        } finally {
            setRedeeming(false);
        }
    };

    if (loading) return (
        <Layout>
            <div className="flex justify-center items-center py-24"><ProgressSpinner /></div>
        </Layout>
    );

    const typeBreakdown = impressions.reduce((acc, imp) => {
        const t = imp.campaign?.ad_type ?? "unknown";
        acc[t] = (acc[t] || 0) + 1;
        return acc;
    }, {});

    return (
        <Layout>
            <Toast ref={toast} position="bottom-right" />
            <div className="content-wrapper">

                {!inHub && (
                <div className="mb-4">
                    <h4 className="fw-bold mb-1">{t('billing.ad_earnings')}</h4>
                    <p className="text-muted text-sm">{t('billing.ad_earnings_sub')}</p>
                </div>
                )}

                {/* Stat cards */}
                <div className="row g-4 mb-4">
                    <StatCard icon="bx-sun"        label={t('billing.today')}      value={`${earnings?.today_points ?? 0} pts`}  sub={`≈ ${earnings?.today_sar ?? '0.00'} SAR`}  color="warning" />
                    <StatCard icon="bx-calendar"   label={t('billing.this_month')} value={`${earnings?.month_points ?? 0} pts`}  sub={`≈ ${earnings?.month_sar ?? '0.00'} SAR`}  color="info" />
                    <StatCard icon="bx-wallet"     label={t('billing.total')}      value={`${earnings?.total_points ?? 0} pts`}  sub={`≈ ${earnings?.total_sar ?? '0.00'} SAR`}  color="success" />
                    <StatCard icon="bx-star"       label={t('billing.balance')}    value={`${pointsBalance} pts`}                sub={t('billing.redeemable')}                    color="primary" />
                </div>

                {/* The split, served by the API from the SAME config the ledger
                    bills on — never a literal here, which is how a page ends up
                    advertising a share the money does not honour. */}
                {lastUpdated && (
                    <p className="text-muted text-sm mb-2" data-testid="earnings-freshness">
                        {t('billing.updated_ago', { seconds: secondsAgo })}
                    </p>
                )}

                {earnings?.cafe_share_percent != null && (
                    <p className="text-muted text-sm mb-4">
                        {t('billing.revenue_split_note', {
                            cafe: earnings.cafe_share_percent,
                            platform: earnings.platform_share_percent,
                        })}
                    </p>
                )}

                <div className="row g-4 mb-4">
                    {/* Daily chart */}
                    <div className="col-12 col-lg-8">
                        <div className="card h-100">
                            <div className="card-body">
                                <h6 className="fw-semibold mb-3">{t('billing.daily_earnings_chart')}</h6>
                                <BarChart data={earnings?.daily ?? []} noDataLabel={t('billing.no_data_yet')} />
                            </div>
                        </div>
                    </div>

                    {/* Redeem + type breakdown */}
                    <div className="col-12 col-lg-4">
                        <div className="card mb-4">
                            <div className="card-body">
                                <h6 className="fw-semibold mb-2">{t('billing.redeem_points')}</h6>
                                <p className="text-muted text-xs mb-3">{t('billing.balance_label')}: <strong>{pointsBalance} pts</strong></p>
                                <div className="input-group">
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder={t('billing.amount_to_redeem')}
                                        value={redeemAmount}
                                        onChange={e => setRedeemAmount(e.target.value)}
                                        min={1}
                                        max={pointsBalance}
                                    />
                                    <Button
                                        label={redeeming ? "…" : t('billing.redeem')}
                                        loading={redeeming}
                                        disabled={!redeemAmount || parseInt(redeemAmount, 10) < 1}
                                        onClick={redeem}
                                        className="p-button-primary"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Breakdown by type */}
                        <div className="card">
                            <div className="card-body">
                                <h6 className="fw-semibold mb-3">{t('billing.impressions_by_type')}</h6>
                                {Object.keys(typeBreakdown).length === 0 ? (
                                    <p className="text-muted text-sm">{t('billing.no_impressions_yet')}</p>
                                ) : (
                                    <div className="space-y-2">
                                        {Object.entries(typeBreakdown).map(([type, count]) => (
                                            <div key={type} className="flex items-center justify-between">
                                                <Tag value={type.replace("_", " ")} severity={TYPE_SEVERITY[type] ?? "info"} className="text-xs capitalize" />
                                                <span className="text-sm fw-medium">{count}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent impressions table */}
                <div className="card">
                    <div className="card-body">
                        <h6 className="fw-semibold mb-3">{t('billing.recent_impressions')}</h6>
                        {impressions.length === 0 ? (
                            <p className="text-muted text-sm text-center py-4">{t('billing.no_impressions_recorded')}</p>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th className="text-muted text-xs">{t('billing.col_campaign')}</th>
                                                <th className="text-muted text-xs">{t('table.type')}</th>
                                                <th className="text-muted text-xs">{t('billing.col_device')}</th>
                                                <th className="text-muted text-xs">{t('billing.col_points')}</th>
                                                <th className="text-muted text-xs">{t('billing.col_time')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {impressions.map(imp => (
                                                <tr key={imp.id}>
                                                    <td className="text-sm">{imp.campaign?.name ?? "—"}</td>
                                                    <td>
                                                        <Tag value={imp.campaign?.ad_type?.replace("_", " ") ?? "—"}
                                                            severity={TYPE_SEVERITY[imp.campaign?.ad_type] ?? "info"}
                                                            className="text-xs capitalize" />
                                                    </td>
                                                    <td className="text-sm">{imp.device?.name ?? "—"}</td>
                                                    <td className="text-sm fw-medium text-success">{imp.points_awarded}</td>
                                                    <td className="text-xs text-muted">{formatDateTime(imp.created_at)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {impMeta.last_page > 1 && (
                                    <div className="flex justify-center gap-2 mt-3">
                                        <Button icon="pi pi-chevron-left" className="p-button-outlined p-button-sm"
                                            disabled={impPage === 1} onClick={() => setImpPage(p => p - 1)} />
                                        <span className="text-xs self-center text-muted">
                                            {t('billing.page_of', { current: impMeta.current_page, last: impMeta.last_page })}
                                        </span>
                                        <Button icon="pi pi-chevron-right" className="p-button-outlined p-button-sm"
                                            disabled={impPage >= impMeta.last_page} onClick={() => setImpPage(p => p + 1)} />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}
